import { CommonModule } from "@angular/common";
import { Component, Input, OnDestroy } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import { FieldErrorWithValue } from "src/app/models/FieldErrorWithValue";

@Component({
  selector: "app-form-field-error",
  templateUrl: "./form-field-error.component.html",
  styleUrls: ["./form-field-error.component.scss"],
  standalone: true,
  imports: [CommonModule, MatFormFieldModule],
})
export class FormFieldErrorComponent implements OnDestroy {
  private _control: AbstractControl;
  private subscription: Subscription;
  constructor(private translationService: TranslateService) {}
  @Input()
  set control(formControl: AbstractControl) {
    this._control = formControl;
    this.errorMessage = this.getErrorMessage();

    this.unsubscribe();

    this.subscription = formControl?.statusChanges.subscribe(() => {
      this.errorMessage = this.getErrorMessage();
    });
  }
  get control(): AbstractControl {
    return this._control;
  }

  errorMessage: string | null;

  getErrorMessage(): string | null {
    if (this.control?.errors) {
      for (const errorKey in this.control.errors) {
        return this.createError(errorKey, this.control.errors[errorKey]);
      }
    }
  }

  private createError(errorName: string, error: unknown): string {
    return (
      this.getErrorTranslation(errorName, error) ||
      this.getErrorTranslationWithValue(error) ||
      this.translationService.instant(LocalizationConstants.VALIDATION_GENERIC_MESSAGE)
    );
  }

  private getErrorTranslation(errorName: string, error: unknown): string | null {
    switch (errorName) {
      case "required":
        return this.translationService.instant(LocalizationConstants.LABEL_REQUIRED);
      case "min":
        return this.translationService.instant(LocalizationConstants.VALIDATION_MIN_MESSAGE, {
          value: (error as { min: number }).min,
        });
      case "max":
        return this.translationService.instant(LocalizationConstants.VALIDATION_MAX_MESSAGE, {
          value: (error as { max: number }).max,
        });
      case "maxlength":
        return this.translationService.instant(
          LocalizationConstants.VALIDATION_MAX_LENGTH_MESSAGE,
          {
            value: (error as { requiredLength: number }).requiredLength,
          },
        );
      // TODO: add more cases for other angular validation errors - pattern, email, etc.
      default:
        return typeof error === "string" ? this.translationService.instant(error) : null;
    }
  }

  /**
   * Translation with value must implement @interface FieldErrorWithValue
   */
  private getErrorTranslationWithValue(error: unknown): string | null {
    if (!this.isFieldErrorWithValue(error)) return null;
    return this.translationService.instant(error.localizationKey, { value: error.value });
  }

  private isFieldErrorWithValue(error: unknown): error is FieldErrorWithValue {
    return typeof error === "object" && "localizationKey" in error && "value" in error;
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  unsubscribe(): void {
    if (!this.subscription) return;

    this.subscription.unsubscribe();
  }
}
